/**
 * Custom hook for image validation with state management and toast notifications
 * Provides a reusable interface for validating image files across components
 */

import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { 
  validateImageFile, 
  validateImageFileWithToast,
  showValidationError 
} from '../utils/imageValidation';
import { ERROR_MESSAGES, IMAGE_VALIDATION_CONFIG } from '../constants/imageUpload';

/**
 * Custom hook for image validation
 * @returns {Object} - Validation functions and state
 */
export const useImageValidation = () => {
  // State management for validation results
  const [errors, setErrors] = useState([]);
  const [isValid, setIsValid] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [validatedFiles, setValidatedFiles] = useState([]);
  
  // Validate multiple files with toast notifications
  const validateFiles = useCallback((files, showToast = true) => {
    if (!files || files.length === 0) {
      const error = ERROR_MESSAGES.NO_FILE;
      setErrors([error]);
      setIsValid(false);
      if (showToast) {
        toast.error(error);
      }
      return { isValid: false, errors: [error], validFiles: [] };
    }

    setIsValidating(true);
    const allErrors = [];
    const validFiles = [];

    // Convert FileList to Array if needed
    const fileArray = Array.from(files);

    fileArray.forEach((file, index) => {
      const validation = validateImageFile(file);
      
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        allErrors.push(...validation.errors.map(error => `File ${index + 1}: ${error}`));
        
        // Show toast for each invalid file if enabled
        if (showToast) {
          validation.errors.forEach(error => {
            toast.error(`${file.name}: ${error}`);
          });
        }
      }
    });

    // Update state
    setErrors(allErrors);
    setIsValid(allErrors.length === 0);
    setValidatedFiles(validFiles);
    setIsValidating(false);

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
      validFiles,
      totalFiles: fileArray.length,
      validCount: validFiles.length,
      invalidCount: fileArray.length - validFiles.length
    };
  }, []);

  // Validate single file with toast notifications
  const validateSingleFile = useCallback((file, showToast = true) => {
    if (!file) {
      const error = ERROR_MESSAGES.NO_FILE;
      setErrors([error]);
      setIsValid(false);
      if (showToast) {
        toast.error(error);
      }
      return { isValid: false, errors: [error] };
    }

    setIsValidating(true);
    
    const validation = showToast 
      ? validateImageFileWithToast(file)
      : validateImageFile(file);

    // Update state
    setErrors(validation.errors);
    setIsValid(validation.isValid);
    setValidatedFiles(validation.isValid ? [file] : []);
    setIsValidating(false);

    return validation;
  }, []);

  // Clear all validation errors and reset state
  const clearErrors = useCallback(() => {
    setErrors([]);
    setIsValid(true);
    setValidatedFiles([]);
    setIsValidating(false);
  }, []);

  // Set custom error message
  const setCustomError = useCallback((error, showToast = true) => {
    const errorMessage = typeof error === 'string' ? error : ERROR_MESSAGES.INVALID_FILE;
    setErrors([errorMessage]);
    setIsValid(false);
    setValidatedFiles([]);
    
    if (showToast) {
      toast.error(errorMessage);
    }
  }, []);

  // Reset validation state (clears errors but keeps validated files)
  const resetValidation = useCallback(() => {
    setErrors([]);
    setIsValid(true);
    setIsValidating(false);
  }, []);

  // Get validation summary
  const getValidationSummary = useCallback(() => {
    return {
      hasErrors: errors.length > 0,
      errorCount: errors.length,
      isValid,
      isValidating,
      validFileCount: validatedFiles.length,
      errors
    };
  }, [errors, isValid, isValidating, validatedFiles]);
  
  return {
    // State
    errors,
    isValid,
    isValidating,
    validatedFiles,
    
    // Validation functions
    validateFiles,
    validateSingleFile,
    
    // State management functions
    clearErrors,
    setCustomError,
    resetValidation,
    
    // Utility functions
    getValidationSummary,
    
    // Configuration access
    config: IMAGE_VALIDATION_CONFIG
  };
};
